import Phaser from 'phaser';
import { formatMs } from '../utils/time';

export class HUD extends Phaser.Scene {
  private timerText!: Phaser.GameObjects.Text;
  private waterText!: Phaser.GameObjects.Text;
  private gameplay?: Phaser.Scene;
  private _lastText: string = '';
  private _lastWaterShown: number = -1;
  private parentKey: string = 'GameScene';

  // Ammo HUD state
  private bulletIcons: Phaser.GameObjects.GameObject[] = [];
  private _lastShotsShown: number = -1;
  private _lastHasPistol: boolean = false;

  constructor() {
    super({ key: 'HUD' });
  }

  init(data?: { parentKey?: string }) {
    // Save parent key for later re-resolution; default to GameScene
    this.parentKey = (data && data.parentKey) ? data.parentKey : 'GameScene';
    try {
      this.gameplay = this.scene.get(this.parentKey) as Phaser.Scene;
    } catch {
      this.gameplay = undefined; // gameplay might not be available yet
    }
  }

  create() {
    // Reset internal caches on (re)create to avoid stale HUD state after restarts
    this._lastText = '';
    this._lastWaterShown = -1;
    this._lastShotsShown = -1;
    this._lastHasPistol = false;
    this.bulletIcons = [];

    // Water text (top-left)
    this.waterText = this.add
      .text(16, 16, 'Water: 0s', {
        fontSize: '24px',
        color: '#ffffff'
      })
      .setScrollFactor(0)
      .setDepth(1000);

    this.waterText.setStroke('#000000', 2);
    this.waterText.setShadow(2, 2, '#000000', 2, true, true);

    // Survival timer text (under water)
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    };
    this.timerText = this.add.text(16, 42, '00:00.00', style).setScrollFactor(0).setDepth(1000);

    // Bullet icons: 5 in a horizontal line under the timer
    this.createBulletIcons(16, 66, 5, 22);

    // Ensure HUD renders above gameplay scene (also redundantly handled by GameScene)
    try {
      this.scene.bringToTop('HUD');
    } catch {
      // no-op if bringToTop fails
    }

    // Immediate initial sync so HUD reflects current game state on the very first frame
    try {
      if (!this.gameplay) {
        try {
          this.gameplay = this.scene.get(this.parentKey) as Phaser.Scene;
        } catch {
          // will sync on next update if gameplay not ready yet
        }
      }
      const anyGame = this.gameplay as any;
      const shots = typeof anyGame?.getPlayerShots === 'function' ? (anyGame.getPlayerShots() as number) : 0;
      const hasPistol = typeof anyGame?.getHasPistol === 'function' ? !!anyGame.getHasPistol() : false;

      this.updateBulletIcons(shots, hasPistol);
      this._lastShotsShown = shots;
      this._lastHasPistol = hasPistol;

      // Minimal debug log to aid verification
      // eslint-disable-next-line no-console
      console.info('[HUD] Initial ammo sync', { shots, hasPistol, parentKey: this.parentKey });
    } catch {
      // ignore; update() will retry
    }
  }

  private createBulletIcons(startX: number, y: number, count: number, spacing: number) {
    // Try to use a small weapon icon; otherwise fallback to rectangles
    const textureKey = 'weapon_gun';
    const hasTexture = this.textures.exists(textureKey);

    for (let i = 0; i < count; i++) {
      const x = startX + i * spacing;
      let icon: Phaser.GameObjects.GameObject;
      if (hasTexture) {
        const img = this.add.image(x, y, textureKey)
          .setOrigin(0, 0)
          .setScrollFactor(0)
          .setDepth(1000);
        // scale to small HUD size
        const targetHeight = 14;
        const scale = targetHeight / Math.max(1, img.height);
        img.setScale(scale);
        // slight tint for readability over backgrounds
        img.setTint(0xffffaa);
        icon = img;
      } else {
        const rect = this.add.rectangle(x, y, 14, 6, 0xffff66)
          .setOrigin(0, 0)
          .setScrollFactor(0)
          .setDepth(1000);
        rect.setStrokeStyle(2, 0x000000, 0.8);
        icon = rect;
      }
      this.bulletIcons.push(icon);
      // start hidden; update() will reveal as needed
      (icon as any).setVisible?.(false);
    }
  }

  private updateBulletIcons(shots: number, hasPistol: boolean) {
    const clamped = hasPistol ? Math.max(0, Math.min(this.bulletIcons.length, shots | 0)) : 0;
    for (let i = 0; i < this.bulletIcons.length; i++) {
      const visible = i < clamped;
      (this.bulletIcons[i] as any).setVisible?.(visible);
    }
  }

  update() {
    // Re-resolve gameplay if missing, to be resilient across restarts
    if (!this.gameplay) {
      try {
        this.gameplay = this.scene.get(this.parentKey) as Phaser.Scene;
      } catch {
        // keep undefined until available
      }
    }

    try {
      // Safely read survival ms
      const ms = (this.gameplay as any && (this.gameplay as any).getSurvivalMs)
        ? (this.gameplay as any).getSurvivalMs()
        : 0;
      const next = formatMs(ms);
      if (next !== this._lastText) {
        this._lastText = next;
        this.timerText.setText(next);
      }

      // Safely read water seconds from gameplay scene
      const water = Math.max(0, Math.floor(((this.gameplay as any)?.currentWaterSeconds) || 0));
      if (water !== this._lastWaterShown) {
        this._lastWaterShown = water;
        this.waterText.setText(`Water: ${water}s`);
      }

      // Ammo HUD: query GameScene getters
      const anyGame = this.gameplay as any;
      const shots = typeof anyGame?.getPlayerShots === 'function' ? (anyGame.getPlayerShots() as number) : 0;
      const hasPistol = typeof anyGame?.getHasPistol === 'function' ? !!anyGame.getHasPistol() : false;

      if (shots !== this._lastShotsShown || hasPistol !== this._lastHasPistol) {
        this._lastShotsShown = shots;
        this._lastHasPistol = hasPistol;
        this.updateBulletIcons(shots, hasPistol);
      }
    } catch {
      // Swallow errors to avoid breaking the update loop; HUD will retry next frame
    }
  }
}